/* ⚠️ GENERATED from app/services/sendState.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/sendState.ts — what a chat row should say about my own last message.
//
// 🟥 FIVE SYSTEMS PAINT THAT ROW and they did not share a vocabulary. The bubble
//    knew `paused`; the list knew `failed`, `uploading` and `success`. A photo
//    sent with no internet is paused, so the chat showed the red ! and the chat
//    tab showed a progress ring at 0% — a number that could never move, on a
//    send that was already over. The rule lives here now, in one place, as a
//    function that can be asked the question directly.
//
// 🟢 PURE. It takes a stored message row — the DURABLE one, off disk — and
//    returns what the row should draw, or null for "this is not an upload, use
//    the ordinary ticks". A twin is generated for the web.

/** The message types that upload bytes and therefore own the row's indicator. */
export const UPLOADING_TYPES = ['image', 'video', 'images', 'audio', 'document'];

/**
 * 🟥 ONLY THESE MEAN "NOT GOING TO HAPPEN BY ITSELF": a send the server refused or never got after
 *    we asked (services/sendOutcome — evidence, not a clock), and one the person cancelled.
 *
 * 🟥 `paused` AND `retry_available` WERE ON THIS LIST, AND WITH THEM EVERY SEND MADE WITH NO
 *    SIGNAL. "I sent it without Wi-Fi: the chat shows the clock, which is correct — the chats tab
 *    shows the red !." Those two are the OFFLINE states: the upload is waiting for a network and
 *    resumes by itself when one comes back. WhatsApp — and this app's own bubble
 *    (stores/messageStatus: every pre-server phase is the clock) — call that waiting. So does every
 *    surface now: the chat, the chats tab, the tile view, the forward list, the widget, the web.
 */
const DEAD = ['failed'];

/** Waiting for a network (an upload paused by the link going away) — the clock, never the red !. */
export function isWaitingSend(row) {
  if (!row) return false;
  return row.uploadStatus === 'paused' || row.uploadStatus === 'retry_available';
}
const isFinite0 = v => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * What the chat row should draw for my own newest message, or null when the
 * ordinary tick logic (clock / one check / two checks) should decide instead.
 *
 * @param row     the stored message, off disk — never a React-state copy, which
 *                is exactly the distinction this bug was made of
 * @param livePct a percentage from the in-memory upload tracker, when it has one
 *                and has not gone terminal; the stored row's own is the fallback
 */
/**
 * Has this send failed (or been cancelled) — the red !?
 *
 * 🟢 THE SAME QUESTION BOTH CLIENTS ASK, so the browser's tick and the phone's list can never
 *    disagree. Waiting for a network is NOT a failure (isWaitingSend).
 */
export function isDeadSend(row) {
  if (!row) return false;
  return DEAD.includes(String(row.uploadStatus || '')) || !!row._uploadCancelled || !!row.sendFailed;
}
export function rowSendState(row, livePct, opts) {
  if (!row || !UPLOADING_TYPES.includes(String(row.type || ''))) return null;

  /* 🟥 CONFIRMED IS CHECKED FIRST, AND IT DID NOT USE TO BE. These two tests were
     the other way round, so a row carrying BOTH a server `seq` and a stale
     `uploadStatus: 'failed'` drew the red ! — and that combination is not
     hypothetical, it is the reported bug's resting state: the send landed, a
     timer or a chat-list write declared it failed, the ack arrived a moment
     later and the flag was never cleared. Evidence beats a leftover flag. */
  if (row.seq != null || row.sent === true || row.uploadStatus === 'success') return null;
  if (isDeadSend(row)) return {
    kind: 'failed'
  };

  /* 📴 waiting for a network — paused, or offline right now with the bytes going nowhere: no ring
     (a ring that cannot move says "still going"), the ordinary ticks draw the clock */
  if (isWaitingSend(row) || opts?.offline) return null;
  const inFlight = row.uploadStatus === 'uploading' || row.isSending === true;
  if (!inFlight) return null;
  const pct = Number.isFinite(Number(livePct)) && Number(livePct) >= 0 ? Number(livePct) : isFinite0(row.uploadProgress);
  /* 🟢 never 100: the ring belongs to bytes in flight, and the handoff to a real
     tick is what says "done" */
  return {
    kind: 'uploading',
    pct: Math.max(0, Math.min(99, Math.round(pct)))
  };
}

/**
 * Should the chat row show the red ! for my newest message?
 *
 * 🟥 OFFLINE USED TO BE ENOUGH. A text sent with no signal was declared failed on the list ("the
 *    ! is early rather than wrong") while the chat, correctly, drew the clock — two answers to
 *    "did this go?" for the same message. A message that has not reached the server is WAITING,
 *    whatever the network: no Wi-Fi, Wi-Fi with no internet, airplane mode, our server
 *    unreachable. It goes by itself when it can, and the list says so with the clock, like the
 *    chat. The red ! is a verdict with evidence (services/sendOutcome) or the person's own cancel.
 *
 * 🟢 EVERY TYPE THE SAME — text, photo, video, album, voice, document, location, contact, poll.
 */
export function rowTickFailed(row) {
  if (!row) return false;
  /* 🟥 A message the server has is not a failed message, whatever flag is still stuck to it. */
  if (row.seq != null || row.sent === true || row.uploadStatus === 'success') return false;
  return isDeadSend(row);
}

/** How many items' worth of a batch have actually gone.
 *
 * 🟥 THE COUNTER AND THE PERCENTAGE USED TO MEASURE DIFFERENT THINGS — completed
 *    items against the mean of all of them — so an album uploading over parallel
 *    lanes read "0/10" while the bar passed 30%. Derived from the same sum, they
 *    cannot disagree.
 */
export function itemsDone(totalProgress, totalCount) {
  const n = Math.max(0, Math.round(Number(totalCount) || 0));
  if (n <= 0) return 0;
  const p = Math.max(0, Math.min(100, Number(totalProgress) || 0));
  return Math.min(n, Math.max(1, Math.round(p * n / 100)));
}
